// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState, useEffect } from 'react';
import Modal from 'antd/lib/modal';
import Checkbox from 'antd/lib/checkbox';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import { DownloadOutlined } from '@ant-design/icons';
import { getCore } from 'cvat-core-wrapper';

const core = getCore();

interface JobLabelData {
    jobId: number;
    taskId: number;
    labelCounts: Record<string, number>;
    frameCount: number;
}

interface Props {
    taskId?: number;
    projectId?: number;
    runId?: number;
    visible: boolean;
    onClose: () => void;
}

async function fetchLabelCounts(jobId: number): Promise<Record<string, number>> {
    const res = await fetch(`/api/labeling-runs/job-label-counts/${jobId}`, { credentials: 'same-origin' });
    if (!res.ok) return {};
    return res.json();
}

async function fetchAllJobs(taskId: number): Promise<any[]> {
    const allJobs: any[] = [];
    let page = 1;
    let total = Infinity;
    while (allJobs.length < total) {
        const batch = await core.jobs.get({ taskID: taskId, page });
        total = batch.count;
        allJobs.push(...batch);
        page += 1;
    }
    return allJobs.filter((j: any) => j.type === 'annotation');
}

export default function LabelCountsDownloadModal({
    taskId, projectId, runId, visible, onClose,
}: Props): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [allLabels, setAllLabels] = useState<string[]>([]);
    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [jobData, setJobData] = useState<JobLabelData[]>([]);
    const [includeFrameCount, setIncludeFrameCount] = useState(false);

    const sourceId = taskId ?? projectId ?? runId;

    useEffect(() => {
        if (!visible) return;

        setLoading(true);
        setAllLabels([]);
        setSelectedLabels([]);
        setJobData([]);

        (async () => {
            try {
                const results: JobLabelData[] = [];
                const labelSet = new Set<string>();

                const processJobs = async (jobs: any[], tid: number): Promise<void> => {
                    await Promise.all(jobs.map(async (job: any) => {
                        const counts = await fetchLabelCounts(job.id);
                        for (const label of Object.keys(counts)) labelSet.add(label);
                        const frameCount = (job.stopFrame ?? job.stop_frame ?? 0) -
                            (job.startFrame ?? job.start_frame ?? 0) + 1;
                        results.push({ jobId: job.id, taskId: tid, labelCounts: counts, frameCount });
                    }));
                };

                if (taskId !== undefined) {
                    const [task] = await core.tasks.get({ id: taskId });
                    for (const label of (task.labels ?? [])) labelSet.add(label.name);
                    const jobs = await fetchAllJobs(taskId);
                    await processJobs(jobs, taskId);
                } else if (projectId !== undefined) {
                    const allTasks: any[] = [];
                    let nextUrl: string | null = `/api/tasks?project_id=${projectId}&page_size=100`;
                    while (nextUrl) {
                        const res = await fetch(nextUrl, { credentials: 'same-origin' });
                        const data = await res.json();
                        allTasks.push(...(data.results || []));
                        nextUrl = data.next || null;
                    }
                    await Promise.all(allTasks.map(async (task: any) => {
                        const [taskObj] = await core.tasks.get({ id: task.id });
                        for (const label of (taskObj.labels ?? [])) labelSet.add(label.name);
                        const jobs = await fetchAllJobs(task.id);
                        await processJobs(jobs, task.id);
                    }));
                } else if (runId !== undefined) {
                    const res = await fetch(`/api/labeling-runs/${runId}/tasks`, { credentials: 'same-origin' });
                    const runTasks: any[] = await res.json();
                    await Promise.all(runTasks.map(async (runTask: any) => {
                        const jobs = await fetchAllJobs(runTask.task_id);
                        await processJobs(jobs, runTask.task_id);
                    }));
                }

                const labels = Array.from(labelSet).sort();
                setAllLabels(labels);
                setSelectedLabels(labels);
                setJobData(results);
            } finally {
                setLoading(false);
            }
        })();
    }, [visible, sourceId]);

    const handleDownload = (): void => {
        const origin = window.location.origin;
        const escape = (s: string): string => `"${String(s).replace(/"/g, '""')}"`;
        const row = (cells: string[]): string => cells.map(escape).join(',');
        const emptyRow = (): string => '';

        const jobUrl = ({ jobId, taskId: tid }: JobLabelData): string =>
            `${origin}/tasks/${tid}/jobs/${jobId}`;

        const sortedJobData = [...jobData].sort((a, b) => (
            a.taskId !== b.taskId ? a.taskId - b.taskId : a.jobId - b.jobId
        ));
        const lines: string[] = [];

        // Table 1: Label Counts
        lines.push(row(['Label Counts']));
        lines.push(row(['Job', ...selectedLabels, 'Total']));
        for (const entry of sortedJobData) {
            const counts = selectedLabels.map((l) => entry.labelCounts[l] ?? 0);
            const total = counts.reduce((s, n) => s + n, 0);
            lines.push(row([jobUrl(entry), ...counts.map(String), String(total)]));
        }

        // Table 2: Frame Counts (optional)
        if (includeFrameCount) {
            lines.push(emptyRow());
            lines.push(emptyRow());
            lines.push(row(['Frame Counts']));
            lines.push(row(['Job', 'Frame Count']));
            let totalFrames = 0;
            for (const entry of sortedJobData) {
                lines.push(row([jobUrl(entry), String(entry.frameCount)]));
                totalFrames += entry.frameCount;
            }
            lines.push(row(['Total', String(totalFrames)]));
        }

        const csv = lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = taskId !== undefined
            ? `label-counts-task-${taskId}.csv`
            : projectId !== undefined
                ? `label-counts-project-${projectId}.csv`
                : `label-counts-run-${runId}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const allChecked = selectedLabels.length === allLabels.length;
    const indeterminate = selectedLabels.length > 0 && !allChecked;

    return (
        <Modal
            title='Download Label Counts'
            open={visible}
            onCancel={onClose}
            footer={(
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Checkbox
                        checked={includeFrameCount}
                        onChange={(e) => setIncludeFrameCount(e.target.checked)}
                    >
                        Download frame count
                    </Checkbox>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type='primary'
                            icon={<DownloadOutlined />}
                            disabled={loading || selectedLabels.length === 0}
                            onClick={handleDownload}
                        >
                            Download CSV
                        </Button>
                    </div>
                </div>
            )}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '24px' }}>
                    <Spin tip='Loading job data...' />
                </div>
            ) : allLabels.length === 0 ? (
                <Text type='secondary'>No label data found.</Text>
            ) : (
                <>
                    <Text type='secondary' style={{ display: 'block', marginBottom: '12px' }}>
                        {`Select labels to include (${jobData.length} jobs):`}
                    </Text>
                    <Checkbox
                        indeterminate={indeterminate}
                        checked={allChecked}
                        onChange={(e) => setSelectedLabels(e.target.checked ? [...allLabels] : [])}
                        style={{ marginBottom: '8px', fontWeight: 600 }}
                    >
                        Select all
                    </Checkbox>
                    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        <Checkbox.Group
                            value={selectedLabels}
                            onChange={(vals) => setSelectedLabels(vals as string[])}
                            style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                        >
                            {allLabels.map((label) => (
                                <Checkbox key={label} value={label}>{label}</Checkbox>
                            ))}
                        </Checkbox.Group>
                    </div>
                </>
            )}
        </Modal>
    );
}
